/* eslint-disable react/function-component-definition */
import React, { useEffect, createContext, useContext, useMemo } from 'react';
import { SilentRequest, InteractionStatus } from '@azure/msal-browser';
import { useMsal, useAccount } from '@azure/msal-react';
import { AxiosInstance } from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import {
  SgrTchTickets,
  SgrTchClients,
  SgrTchCatalogs,
  SgrTchProducts,
  SgrTchInventory,
  SgrTchGeography,
  SgrTchFileStorage,
  SgrTchSecurity,
  SgrTchMessageCommunication,
  SgrTchInsight,
  SgrTchProcess
} from 'config/scopes';
import type {
  ticket as TicketType,
  catalog as CatalogType,
  client as ClientType,
  geography as GeographyType,
  inventory as InventoryType,
  product as ProductType,
  fileStorage as FileStorageType,
  security as SecurityType,
  messageCommunication as MessageCommunicationType,
  insight as InsightType,
  processApi as ProcessType,
  itilGenius as ItilGeniusType
} from 'api/index';
import {
  ticket,
  catalog,
  client,
  geography,
  inventory,
  product,
  fileStorage,
  security,
  messageCommunication,
  insight,
  processApi,
  itilGenius
} from 'api/index';

import {
  KEY_ERROR_CONNECTION,
  MESSAGE_ERROR_CONNECTION,
  useGlobalContext
} from './GlobalContext';

type ContextTypeValue = {
  ticket: typeof TicketType;
  catalog: typeof CatalogType;
  client: typeof ClientType;
  geography: typeof GeographyType;
  inventory: typeof InventoryType;
  product: typeof ProductType;
  fileStorage: typeof FileStorageType;
  security: typeof SecurityType;
  messageCommunication: typeof MessageCommunicationType;
  insight: typeof InsightType;
  processApi: typeof ProcessType;
  itilGenius: typeof ItilGeniusType;
};

const ApiContext = createContext<ContextTypeValue>({
  ticket,
  catalog,
  client,
  geography,
  inventory,
  product,
  fileStorage,
  security,
  messageCommunication,
  insight,
  processApi,
  itilGenius
});

const PERSIST_AND_PREVENT_DUPLICATE = true;

const ApiRequestProvider: React.FC = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0]);
  const { showError } = useGlobalContext();
  const { closeSnackbar } = useSnackbar();
  const { t } = useTranslation('global');

  window.addEventListener('storage', async () => {
    const key = localStorage.getItem('msal.account.keys');
    if (!key) {
      await instance.logout();
    }
  });

  const intercept = (axios: AxiosInstance, silentRequest: SilentRequest) => {
    axios.interceptors.request.use(async (config) => {
      if (!account) {
        throw Error('No active account! Verify a user has been signed in.');
      }
      if (!navigator.onLine) {
        showError(
          t(MESSAGE_ERROR_CONNECTION),
          PERSIST_AND_PREVENT_DUPLICATE,
          KEY_ERROR_CONNECTION
        );
        throw Error('A connection error occurred');
      }

      closeSnackbar(KEY_ERROR_CONNECTION);

      const response = await instance.acquireTokenSilent({
        ...silentRequest,
        account
      });

      config.headers.Authorization = `Bearer ${response.accessToken}`;

      return config;
    });
  };

  useEffect(() => {
    if (!account && InteractionStatus.None === inProgress && !accounts.length) {
      return;
    }

    intercept(security.instance, { ...SgrTchSecurity });
    intercept(ticket.instance, { ...SgrTchTickets });
    intercept(client.instance, { ...SgrTchClients });
    intercept(catalog.instance, { ...SgrTchCatalogs });
    intercept(product.instance, { ...SgrTchProducts });
    intercept(inventory.instance, { ...SgrTchInventory });
    intercept(geography.instance, { ...SgrTchGeography });
    intercept(fileStorage.instance, { ...SgrTchFileStorage });
    intercept(messageCommunication.instance, { ...SgrTchMessageCommunication });
    intercept(insight.instance, { ...SgrTchInsight });
    intercept(processApi.instance, { ...SgrTchProcess });
    intercept(itilGenius.instance, { ...SgrTchTickets });
    // TODO: Agregar scope y habilitar cuando exista API real
    // intercept(problemCauses.instance, { ...SgrTchProcess });
  }, [account, accounts]);

  const value = useMemo(
    () => ({
      ticket,
      catalog,
      client,
      geography,
      inventory,
      product,
      fileStorage,
      security,
      messageCommunication,
      insight,
      processApi,
      itilGenius
    }),
    []
  );

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

const useApiContext = () => useContext(ApiContext);

export { ApiRequestProvider, useApiContext };
